import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useParams } from 'react-router-dom';
import { getCurrentUser, signOut, fetchAuthSession, fetchUserAttributes } from '@aws-amplify/auth';
import SignIn from './components/SignIn';
import SignUp from './components/SignUp';
import NewPasswordRequired from './components/NewPasswordRequired';
import Navigation from './components/Navigation';
import ConfirmSignUp from './components/ConfirmSignUp';
import HomePage from './HomePage';
import StartTest from './StartTest';
import TestList from './TestList';
import './App.css';
import SubjectDetail from './components/SubjectDetail';
import Dashboard from './components/Dashboard';
import MyTests from './components/MyTests';
import Logo from './components/Logo';
import Terms from './components/Terms';
import Privacy from './components/Privacy';
import Contact from './components/Contact';
import ResetPassword from './components/ResetPassword';
import ConfirmResetPassword from './components/ConfirmResetPassword';
import './styles/theme.css';
import SampleTestList from './components/SampleTestList';

// Protected Route wrapper component
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAuthentication();
  }, []);

  async function checkAuthentication() {
    try {
      await getCurrentUser();
      setIsAuthenticated(true);
    } catch (error) {
      setIsAuthenticated(false);
    }
    setIsLoading(false);
  }

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return isAuthenticated ? children : <Navigate to="/signin" />;
};

// Component to handle confirm signup with email parameter
const ConfirmSignUpWithEmail = ({ onConfirmation }) => {
  const { email } = useParams();
  return <ConfirmSignUp username={email} onConfirmation={onConfirmation} />;
};

function App() {
  const [user, setUser] = useState(null);
  const [authState, setAuthState] = useState('signIn');
  const [challengeResponse, setChallengeResponse] = useState(null);
  const [unconfirmedUsername, setUnconfirmedUsername] = useState(null);

  useEffect(() => {
    checkUser();
  }, []);

  async function checkUser() {
    try {
      const user = await getCurrentUser();
      const session = await fetchAuthSession();
      const userAttributes = await fetchUserAttributes();

      // Create an enhanced user object with attributes
      const enhancedUser = {
        ...user,
        attributes: userAttributes
      };

      setUser(enhancedUser);
      setAuthState('signedIn');
    } catch (error) {
      console.error("Error checking state: ", error);
      setUser(null);
      setAuthState('signIn');
    }
  }

  const handleSignIn = () => {
    console.log("Handling the signin process.....");
    checkUser();
  };

  const handleSignUp = (username) => {
    setUnconfirmedUsername(username);
    setAuthState('confirmSignUp');
  };

  const handleNewPasswordRequired = (nextStep) => {
    setChallengeResponse(nextStep);
    setAuthState('newPasswordRequired');
  };

  const handlePasswordSet = () => {
    setAuthState('signedIn');
    checkUser();
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setUser(null);
      setAuthState('signIn');
    } catch (error) {
      console.error('Error signing out: ', error);
    }
  };

  const handleConfirmation = () => {
    setAuthState('signIn');
  };

  if (authState === 'loading') {
    return <div>Loading...</div>;
  }

  return (
    <Router>
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Navigation user={user} onSignOut={handleSignOut} />
        <main style={{ flex: 1, paddingTop: 'var(--spacing-2xl)' }}>
          <Routes>
            <Route path="/" element={
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            } />
            <Route path="/tests" element={
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            } />
            <Route path="/my-tests" element={
              <ProtectedRoute>
                <TestList />
              </ProtectedRoute>
            } />
            <Route path="/start-test/:testId" element={
              <ProtectedRoute>
                <StartTest />
              </ProtectedRoute>
            } />
            <Route path="/signin" element={
              authState !== 'signedIn' ?
                <SignIn onSignIn={handleSignIn} onNewPasswordRequired={handleNewPasswordRequired} /> :
                <Navigate to="/" replace />
            } />
            <Route path="/signup" element={
              authState !== 'signedIn' ?
                <SignUp onSignUp={handleSignUp} /> :
                <Navigate to="/" replace />
            } />
            <Route path="/confirm-signup" element={
              authState === 'confirmSignUp' ?
                <ConfirmSignUp username={unconfirmedUsername} onConfirmation={handleConfirmation} /> :
                <Navigate to="/signin" replace />
            } />
            <Route path="/confirm-signup/:email" element={
              <ConfirmSignUpWithEmail onConfirmation={handleConfirmation} />
            } />
            <Route path="/new-password" element={
              authState === 'newPasswordRequired' ?
                <NewPasswordRequired challengeResponse={challengeResponse} onPasswordSet={handlePasswordSet} /> :
                <Navigate to="/signin" replace />
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/confirm-reset-password" element={<ConfirmResetPassword />} />
            <Route path="/sample-tests" element={<SampleTestList />} />
          </Routes>
        </main>
        <footer style={{ 
          backgroundColor: 'var(--background)',
          padding: 'var(--spacing-xl) 0',
          marginTop: 'var(--spacing-2xl)',
          borderTop: '1px solid var(--border)'
        }}>
          <div className="container">
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                <Logo size={24} />
                <span style={{ color: 'var(--text-secondary)' }}>
                  © {new Date().getFullYear()} PrepNest. All rights reserved.
                </span>
              </div>
              <div style={{ display: 'flex', gap: 'var(--spacing-lg)' }}>
                <Link to="/privacy" style={{ color: 'var(--text-secondary)' }}>Privacy Policy</Link>
                <Link to="/terms" style={{ color: 'var(--text-secondary)' }}>Terms of Service</Link>
                <Link to="/contact" style={{ color: 'var(--text-secondary)' }}>Contact</Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </Router>
  );
}

export default App;